/**
 * One-shot "auto-open the setup panel" flag for a freshly created event. The
 * create flow sets it right before navigating into the new event; the
 * onboarding panel consumes it once (forceOpen + intro banner) and clears it,
 * so the panel pops open exactly once per new event and stays quiet after.
 * sessionStorage-scoped so it doesn't survive a full browser restart.
 *
 * "quiet" opens the group WITHOUT the "I already put a few things together"
 * intro — the create flow asks for it when the interview briefs could not be
 * carried over, so the intro would claim work the event never received.
 */
const KEY = (eventId: string) => `veltric-setup-autoopen:${eventId}`;

export type SetupAutoOpen = "intro" | "quiet";

export function flagSetupAutoOpen(eventId: string, opts: { intro?: boolean } = {}): void {
  try {
    sessionStorage.setItem(KEY(eventId), opts.intro === false ? "quiet" : "1");
  } catch {
    // sessionStorage unavailable — auto-open is best-effort.
  }
}

/**
 * Returns the flag once (then clears): "intro" | "quiet", or null when the
 * event was not flagged. A legacy "1" (an open tab from before) reads as intro.
 */
export function consumeSetupAutoOpen(eventId: string): SetupAutoOpen | null {
  try {
    const value = sessionStorage.getItem(KEY(eventId));
    if (value) {
      sessionStorage.removeItem(KEY(eventId));
      return value === "quiet" ? "quiet" : "intro";
    }
  } catch {
    // sessionStorage unavailable.
  }
  return null;
}
